import React from 'react';
import Loader from '@/components/common/Loader';
import EntityContentType from '@/constants/EntityContentType';
import { RESOURCE_NOT_FOUND } from '@/lib/constants/error-enums';
import translate from '@/lib/intl/translate';
import { IntegrationNames } from '@/pages/IntegrationsPage/data/integrations';
import getEntitySource from '../lib/getEntitySource';
import useEntityAttachments from '../lib/useEntityAttachments';
import useEntityDetailsData from '../lib/useEntityDetailsData';
import CampaignContent from './CampaignContent';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import ContentFooter from './ContentFooter';
import ContributionContent from './ContributionContent';
import EmailIntegrationContent from './EmailIntegrationContent';
import EntityDetailsFileUploader from './EntityDetailsFileUploader';
import EntityDetailsHeader from './EntityDetailsHeader';
import EntityDetailsSummary from './EntityDetailsSummary';
import EntityDetailsSummaryContent from './EntityDetailsSummaryContent';
import InteractionContent from './InteractionContent';
import TaskContent from './TaskContent';
import TaskHeader from './TaskHeader';

type EntityDetailsContentProps = {
  constituentId?: null | string;
  entityId: string;
  entityType: EntityContentType;
  onClose(...args: unknown[]): unknown;
};

/**
 * @deprecated this should be replaced with composing the entity details
 * using the entity detail components, much like <OpportunityDetailsContent>
 */
export default function EntityDetailsContent({
  constituentId,
  entityId,
  entityType,
  onClose,
}: EntityDetailsContentProps) {
  const {
    constituent,
    entity,
    error,
    hasData,
    labels,
    mutate,
    pledgeDonation,
    softCredits,
    tickets,
  } = useEntityDetailsData({ constituentId, entityId, entityType });

  const { handleUpload } = useEntityAttachments({
    entityId,
    entityType,
  });

  const isCampaign = [
    EntityContentType.CAMPAIGN_CREATED,
    EntityContentType.CAMPAIGN_GOAL_MET,
    EntityContentType.CAMPAIGN_STRETCH_GOAL_ADDED,
    EntityContentType.CAMPAIGN_STRETCH_GOAL_MET,
  ].some((cmp) => cmp === entityType);

  const content = React.useMemo(() => {
    if (error) {
      let text = translate('GENERIC_ERROR');

      // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'info' does not exist on type 'Error'.
      if (error?.info?.errorEnum === RESOURCE_NOT_FOUND) {
        let errorEntity = entityType;

        if (
          [
            EntityContentType.EMAIL_SENT,
            EntityContentType.EMAIL_RECEIVED,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- TODO(ts-migrate)
            // @ts-ignore
          ].includes(entityType)
        ) {
          errorEntity = translate('EMAIL').toLowerCase();
        }

        if (errorEntity === EntityContentType.DONATION) {
          errorEntity = translate('CONTRIBUTION').toLowerCase();
        }

        // Even though "RESOURCE_NOT_FOUND" is a generic 404 error, this
        // should only be popping up in a feed if the feedItem has been deleted
        // and the feed index on elastic search hasn't yet been updated.
        text = translate('FEED_ENTITY_DELETED', {
          entity: errorEntity,
        });

        const { source } = getEntitySource(entity);

        if (source) {
          text = translate('FEED_ENTITY_DELETED_INTEGRATION', {
            entity: entityType,
            // @ts-expect-error TS(7053) TODO(ts-migrate): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            integration: IntegrationNames[source],
          });
        }
      }

      return (
        <div className="border-t border-grey-300 pt-5 text-error-main">
          {text}
        </div>
      );
    }

    // If hasData is true but the entity is falsy, then the entity was
    // deleted. In that case, we want to return null to prevent the child
    // components from throwing errors.
    if (hasData && !entity) {
      return null;
    }

    if (!hasData) {
      return <Loader className="mb-3" isCentered />;
    }

    switch (entityType) {
      case EntityContentType.DONATION:
        return (
          <ContributionContent
            donation={entity}
            labels={labels}
            pledgeDonation={pledgeDonation}
            softCredits={softCredits}
            tickets={tickets}
          />
        );
      case EntityContentType.CAMPAIGN_CREATED:
      case EntityContentType.CAMPAIGN_GOAL_MET:
      case EntityContentType.CAMPAIGN_STRETCH_GOAL_ADDED:
      case EntityContentType.CAMPAIGN_STRETCH_GOAL_MET:
        return <CampaignContent campaign={entity} />;
      case EntityContentType.EMAIL_RECEIVED:
      case EntityContentType.EMAIL_SENT:
        return <EmailIntegrationContent email={entity} />;
      case EntityContentType.INTERACTION:
        return <InteractionContent interaction={entity} />;
      case EntityContentType.TASK:
        return <TaskContent task={entity} />;
      default:
        return null;
    }
  }, [entity, error, entityType, hasData, pledgeDonation]);

  const header = React.useMemo(() => {
    const isTask = entityType === EntityContentType.TASK;

    if (entity && isTask) {
      // @ts-expect-error TS(2739) TODO(ts-migrate): Type '{}' is missing the following properties from... Remove this comment to see the full error message
      return <TaskHeader onClose={onClose} task={entity} />;
    }

    return (
      <EntityDetailsHeader onClose={onClose}>
        {hasData && entity && (
          <EntityDetailsSummary entityId={entityId} entityType={entityType}>
            <EntityDetailsSummaryContent
              constituent={constituent}
              entity={entity}
              entityType={entityType}
            />
          </EntityDetailsSummary>
        )}
      </EntityDetailsHeader>
    );
  }, [constituent, entity, hasData]);

  return (
    <>
      {header}
      <div>
        {content}
        {hasData && !isCampaign && (
          <EntityDetailsFileUploader
            // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'attachments' does not exist on type 'obj... Remove this comment to see the full error message
            attachments={entity?.attachments}
            onUploadFinished={handleUpload}
          />
        )}
        {entity && !error && (
          <ContentFooter
            contentType={entityType}
            entity={entity}
            mutate={mutate}
            onDelete={onClose}
          />
        )}
      </div>
    </>
  );
}

EntityDetailsContent.defaultProps = {
  constituentId: null,
};
